import {Component, OnInit} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

import {ModalService} from '~shared/components/elements/global-modal/modal.service';
import {SidebarService} from '~core/components/sidebar/sidebar.service';
import {LotteryStatusType} from '~core/enum/lottery-status.enum';
import {AuthenticatedUser, Club, Lottery} from '~core/models';
import {SidebarOptions} from '~core/models/reusables/sidebar-options';
import {NavigationService, StorageService} from '~core/services';
import {ScreenSizeService} from '~core/services/screen-size.service';
import {TixxSelectService} from '~core/services/tixx-select/tixx-select.service';

/**
 * Component that displays more information on a single tixx-select event
 */
@UntilDestroy()
@Component({
  selector: 'app-event-administration',
  templateUrl: './event-administration.component.html'
})

export class EventAdministrationComponent implements OnInit {
  public authenticatedUser: AuthenticatedUser;
  public selectedClub: Club;
  public selectedLottery: Lottery;
  public isOpened: boolean = true;
  public activated: string = LotteryStatusType.ACTIVATED;

  public listOfPanels: any = {
    priceCategories: true,
    priceReductions: true,
  };

  public i18nReleaseTitle: string;
  public i18nReleaseParagraph: string;
  public i18nEditTitle: string;
  public i18nEditParagraph: string;
  public i18nInternalTitle: string;
  public i18nInternalParagraph: string;
  public i18nExecutionTitle: string;
  public i18nExecutionParagraph: string;
  public i18nCompleteTitle: string;
  public i18nCompleteParagraph: string;

  constructor(
    private storage: StorageService,
    private navigationService: NavigationService,
    private sidebarService: SidebarService,
    private tixxSelectService: TixxSelectService,
    private modalService: ModalService,
    private screenSizeService: ScreenSizeService,
  ) {
    if (this.screenSizeService.screenSize === 'desktop') {
      this.sidebarService.setSidebar(new SidebarOptions(true, true));
    } else {
      this.sidebarService.setSidebar(new SidebarOptions(false, true));
    }
    this.sidebarService.castIsOpened.pipe(untilDestroyed(this))
      .subscribe(sidebarOptions => this.isOpened = sidebarOptions.isOpened);
    this.navigationService.setCurrentPage('tixxSelectEvent');
    this.navigationService.setCurrentBreadcrumbs(['crm', 'tixxSelectEvent']);
    this.storage.castAuthenticatedUser.pipe(untilDestroyed(this))
      .subscribe((user: AuthenticatedUser) => this.authenticatedUser = user);
    this.storage.castSelectedClub.pipe(untilDestroyed(this))
      .subscribe((club: Club) => this.selectedClub = club);
    this.tixxSelectService.castSelectedLottery.pipe(untilDestroyed(this))
      .subscribe((lottery: Lottery) => this.selectedLottery = lottery);
  }

  ngOnInit(): void {
    this.i18nReleaseTitle = $localize`:@@tixxSelect.modalTitle.releaseTixxEvent:Release Tixx-Select event?`;
    this.i18nReleaseParagraph = $localize`:@@tixxSelect.modalParagraph.releaseTixxEvent:Please note that some settings can not be changed once the event is released to the activated status. Those settings include prices and reductions, as well as general and customer specific quota settings. Are you sure that all data was setup correctly and the event should be released?`;

    this.i18nEditTitle = $localize`:@@tixxSelect.modalTitle.editTixxEvent:Edit Tixx-Select event?`;
    this.i18nEditParagraph = $localize`:@@tixxSelect.modalParagraph.editTixxEvent:Please note that no public requests can be submitted via the Webpage when the event is in the edit mode and until it gets released again. Would you like to change to the edit mode?`;

    this.i18nInternalTitle = $localize`:@@tixxSelect.modalTitle.internalTixxEvent:Close Tixx-Select event?`;
    this.i18nInternalParagraph = $localize`:@@tixxSelect.modalParagraph.internalTixxEvent:Please note that once the internal mode gets activated ticket requests can no longer be submitted via the Webpage. The event cannot be reactivated. Those changes cant be revoked! Would you like to proceed and change the status of the event?`;

    this.i18nExecutionTitle = $localize`:@@tixxSelect.modalTitle.executionTixxEvent:Execute Tixx-Select event?`;
    this.i18nExecutionParagraph = $localize`:@@tixxSelect.modalParagraph.executionTixxEvent:Please note that the execution mode should be entered once ready to execute the lottery. This step cannot be revoked! Would you like to proceed and change the status of the event?`;

    this.i18nCompleteTitle = $localize`:@@tixxSelect.modalTitle.completeTixxEvent:Complete Tixx-Select event?`;
    this.i18nCompleteParagraph = $localize`:@@tixxSelect.modalParagraph.completeTixxEvent:After completing the event it is not possible to return to the execution mode nor to apply any other changes. This action will change the status from all requests which still have the status received to unsuccessful. Are you sure that this event is completed?`;
  }

  public async showReleaseModal(): Promise<void> {
    this.modalService.setModalOptions(this.i18nReleaseTitle, this.i18nReleaseParagraph);
    const promise = await new Promise<boolean>((resolve, reject) => this.modalService.openModal(resolve, reject));
    if (promise) {
      const completedForm = {
        status: {
          name: 'activated'
        },
      };
      this.submitStatus(completedForm);
    }
  }

  public async showEditModal(): Promise<void> {
    this.modalService.setModalOptions(this.i18nEditTitle, this.i18nEditParagraph);
    const promise = await new Promise<boolean>((resolve, reject) => this.modalService.openModal(resolve, reject));
    if (promise) {
      const completedForm = {
        status: {
          name: 'edit'
        },
      };
      this.submitStatus(completedForm);
    }
  }

  public async showInternalModal(): Promise<void> {
    this.modalService.setModalOptions(this.i18nInternalTitle, this.i18nInternalParagraph);
    const promise = await new Promise<boolean>((resolve, reject) => this.modalService.openModal(resolve, reject));
    if (promise) {
      const completedForm = {
        status: {
          name: 'internal'
        },
      };
      this.submitStatus(completedForm);
    }
  }

  public async showExecutionModal(): Promise<void> {
    this.modalService.setModalOptions(this.i18nExecutionTitle, this.i18nExecutionParagraph);
    const promise = await new Promise<boolean>((resolve, reject) => this.modalService.openModal(resolve, reject));
    if (promise) {
      const completedForm = {
        status: {
          name: 'execution'
        },
      };
      this.submitStatus(completedForm);
    }
  }

  public async showCompletionModal(): Promise<void> {
    this.modalService.setModalOptions(this.i18nCompleteTitle, this.i18nCompleteParagraph);
    const promise = await new Promise<boolean>((resolve, reject) => this.modalService.openModal(resolve, reject));
    if (promise) {
      const completedForm = {
        status: {
          name: 'completed'
        },
      };
      this.submitStatus(completedForm);
    }
  }

  public submitStatus(form: any): void {
    this.modalService.setSpinner(true);
    this.tixxSelectService.updateLotteryStatus(this.selectedLottery.id, form).pipe(untilDestroyed(this))
      .subscribe({
        next: data => {
          location.reload();
        },
        error: err => {
          this.modalService.setSpinner(false);
          this.modalService.setStatus(false);
        }
      });
  }

  public retrieveSubmittedTickets(): void {
    this.tixxSelectService.getSubmittedTickets(this.selectedLottery.id).pipe(untilDestroyed(this))
      .subscribe(data => {
        const link = document.createElement('a');
        const blob = new Blob([data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        link.setAttribute('type', 'hidden');
        link.setAttribute('download', `lottery_${this.selectedLottery.id}_export.csv`);
        link.setAttribute('href', url);
        document.body.appendChild(link);
        link.click();
        link.remove();
      });
  }
}
